import axios, { type AxiosInstance, AxiosError, type AxiosRequestConfig } from 'axios';
import { timeout, getTokenExpirationInfo } from '../utils/helpers';

export const GET = 'get' as const;
export const POST = 'post' as const;
export const PUT = 'put' as const;
export const PATCH = 'patch' as const;
export const DELETE = 'delete' as const;

type HttpMethod = typeof GET | typeof POST | typeof PUT | typeof PATCH | typeof DELETE;

interface ExecuteOptions {
  printLog?: boolean;
  printErrorLogs?: boolean;
  maxRetries?: number;
}

interface ExecuteResponse<T = unknown> {
  data?: T;
  raw?: unknown;
  success: boolean;
  errorData?: {
    data: unknown;
    code: string | number;
    error: Error;
    message: string;
  };
}

const config = {
  maxRetries: 0,
  timeouts: [100, 200, 300],
};

// Track if we've already triggered a 401 redirect to prevent multiple redirects
let isRedirectingToLogin = false;

/**
 * Handle 401 Unauthorized errors globally.
 * Clears auth state and redirects to login page.
 */
const handleUnauthorized = () => {
  // Prevent multiple redirects
  if (isRedirectingToLogin) {
    console.log('[Auth] Already redirecting, skipping...');
    return;
  }

  // Don't redirect if we're already on the login page
  if (window.location.pathname === '/login' || window.location.pathname === '/') {
    console.log('[Auth] Already on login page, skipping redirect');
    return;
  }

  // Don't redirect if user is already logged out (no auth state)
  const authState = localStorage.getItem('authState');
  if (!authState) {
    console.log('[Auth] No auth state in localStorage, skipping redirect');
    return;
  }
  
  try {
    const parsed = JSON.parse(authState);
    if (!parsed.isAuthenticated || !parsed.token) {
      console.log('[Auth] Auth state not valid, skipping redirect', { isAuthenticated: parsed.isAuthenticated, hasToken: !!parsed.token });
      return;
    }
    
    // Log token expiration info for debugging
    const tokenInfo = getTokenExpirationInfo(parsed.token);
    const tokenDebugInfo = {
      timestamp: new Date().toISOString(),
      isExpired: tokenInfo.isExpired,
      expiresAt: tokenInfo.expiresAt?.toISOString(),
      expiresIn: tokenInfo.expiresIn,
      issuedAt: tokenInfo.issuedAt?.toISOString(),
    };
    
    console.warn('[Auth] 401 detected with valid auth state. Token info:', tokenDebugInfo);
    
    // Store token info in sessionStorage (survives redirect)
    try {
      sessionStorage.setItem('401_token_info', JSON.stringify(tokenDebugInfo));
    } catch {
      // Ignore storage errors
    }
    
  } catch {
    // Invalid auth state, skip redirect
    console.log('[Auth] Failed to parse auth state, skipping redirect');
    return;
  }

  isRedirectingToLogin = true;

  console.warn('[Auth] Session expired or invalid. Redirecting to login in 1 second...');
  
  // Small delay to ensure logs are visible and to debounce multiple 401s
  setTimeout(() => {
    // Clear auth state from localStorage
    localStorage.removeItem('authState');
    
    // Redirect to login page
    // Using window.location to ensure a full page reload and clean state
    window.location.href = '/login';
  }, 1000);
};

/**
 * Reset the redirect flag (called after successful login)
 */
export const resetAuthRedirectFlag = () => {
  isRedirectingToLogin = false;
};

export class Executer {
  public client: AxiosInstance;

  constructor(options: Record<string, unknown> = {}, token?: string) {
    const defaultOptions = {
      headers: {
        post: {
          'Content-Type': 'application/json',
        },
      },
    };

    const mergedOptions = Object.assign({}, defaultOptions, options);
    this.client = axios.create(mergedOptions);

    // Add response interceptor for 401 handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          
          // Log the 401 error for debugging
          console.warn('[401 Interceptor] Unauthorized request:', {
            url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
          });
          
          // Check if Authorization header was sent
          const authHeader = error.config?.headers?.['Authorization'] || error.config?.headers?.common?.['Authorization'];
          const hasAuthHeader = !!authHeader;
          const authHeaderPreview = authHeader ? `${String(authHeader).substring(0, 20)}...` : 'MISSING';
          
          console.warn('[401 Interceptor] Auth header status:', {
            hasAuthHeader,
            authHeaderPreview,
          });
          
          // Try to get the server's error response
          const serverResponse = error.response?.data;
          const serverMessage = typeof serverResponse === 'object' 
            ? JSON.stringify(serverResponse) 
            : String(serverResponse || 'No response body');
          
          console.warn('[401 Interceptor] Server response:', serverMessage);
          
          // Store debug info in sessionStorage (survives redirect)
          try {
            const debugInfo = {
              timestamp: new Date().toISOString(),
              url,
              method: error.config?.method,
              baseURL: error.config?.baseURL,
              hasAuthHeader,
              authHeaderPreview,
              serverResponse: serverMessage.substring(0, 200), // Truncate for storage
            };
            
            // Get existing debug info or create new array
            const existingDebug = sessionStorage.getItem('401_debug_log');
            const debugLog = existingDebug ? JSON.parse(existingDebug) : [];
            debugLog.push(debugInfo);
            
            // Keep only last 10 entries
            if (debugLog.length > 10) {
              debugLog.shift();
            }
            
            sessionStorage.setItem('401_debug_log', JSON.stringify(debugLog));
          } catch {
            // Ignore storage errors
          }
          
          // Don't redirect for auth endpoints (login, signup, etc.)
          const isAuthEndpoint = url.includes('/auth/') || url.includes('/signin') || url.includes('/signup');
          
          if (!isAuthEndpoint) {
            handleUnauthorized();
          }
        }
        return Promise.reject(error);
      }
    );

    if (token) {
      this.setAccessToken(token);
    }
  }

  async execute<T = unknown>(
    action: HttpMethod,
    url: string,
    data: AxiosRequestConfig | unknown = {},
    options: ExecuteOptions = {},
    numOfRetries = 0
  ): Promise<ExecuteResponse<T>> {
    const defaultOptions: ExecuteOptions = {
      printLog: false,
      printErrorLogs: true,
      maxRetries: config.maxRetries,
    };

    const mergedOptions = Object.assign({}, defaultOptions, options);

    try {
      let response;
      
      // Handle different axios method signatures
      if (action === POST || action === PUT || action === PATCH) {
        // POST/PUT/PATCH: axios.post(url, data, config)
        const config = data as AxiosRequestConfig;
        const requestData = config.data || config;
        const requestConfig = config.data ? { ...config, data: undefined } : {};
        response = await this.client[action](url, requestData, requestConfig);
      } else {
        // GET/DELETE: axios.get(url, config)
        response = await this.client[action](url, data as AxiosRequestConfig);
      }

      if (mergedOptions.printLog) {
        console.log('API Call:', { url, response, action, data });
      }

      const dataRes = response.data?.data !== undefined ? response.data.data : response.data;

      return {
        data: dataRes,
        raw: response.data,
        success: true,
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      
      if (
        axiosError.response?.status &&
        axiosError.response.status >= 500 &&
        numOfRetries < (mergedOptions.maxRetries || 0)
      ) {
        console.error('API retry:', { url, attempt: numOfRetries });
        await timeout(config.timeouts[numOfRetries]);
        return await this.execute<T>(action, url, data, mergedOptions, numOfRetries + 1);
      }

      if (mergedOptions.printErrorLogs) {
        console.warn('API Error:', { url, error: axiosError.message, action, data });
      }

      return {
        success: false,
        errorData: this._generateError({ url, error: axiosError, action, data }),
      };
    }
  }

  setAccessToken(accessToken: string): void {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  }

  private _generateError(apiLog: {
    url: string;
    error: AxiosError;
    action: HttpMethod;
    data: unknown;
  }) {
    const dataRes = apiLog.error.response?.data || {};
    let code: string | number;
    let message: string;

    if (!apiLog.error.response) {
      code = 'ERR_NETWORK';
      message = apiLog.error.message || 'Network error occurred';
    } else {
      const responseData = (apiLog.error.response.data ?? {}) as Record<string, unknown>;
      code = (responseData.code as string | number) || apiLog.error.response.status || -1;

      const fromMessage = typeof responseData.message === 'string' ? responseData.message.trim() : '';
      const errField = responseData.error;
      const fromErrorString = typeof errField === 'string' ? errField.trim() : '';
      const fromNested =
        errField &&
        typeof errField === 'object' &&
        'message' in errField &&
        typeof (errField as { message: unknown }).message === 'string'
          ? String((errField as { message: string }).message).trim()
          : '';

      message =
        fromMessage ||
        fromErrorString ||
        fromNested ||
        'Unknown error occurred';
    }

    return {
      data: dataRes,
      code,
      error: apiLog.error,
      message,
    };
  }
}

